import { CartAddEvent, DiscountUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Lightweight add-to-cart handler for upsell products.
 *
 * Intentionally does NOT use <product-form-component> or <form action="/cart/add">.
 * This provides full DOM isolation from third-party scripts (e.g. subscription apps)
 * that scan the page for product forms to initialize their widgets.
 *
 * Cart drawer updates work normally: section IDs are collected from
 * cart-items-component elements (same mechanism as product-form.js) and passed
 * to /cart/add.js, whose response sections are forwarded via CartAddEvent.
 *
 * Discount gating
 * ---------------
 * The upsell discount code is a NATIVE Shopify discount code, which (by Shopify
 * design) applies to every matching line in the cart and persists on the cart
 * session even after the original line is removed. To make the code behave as
 * "upsell only", we:
 *
 *  1. Tag upsell-added lines with a private `_upsell: 'true'` line item property.
 *  2. Apply UPSELL30 only after the upsell click.
 *  3. Auto-remove UPSELL30 from the cart whenever it's present but no line is
 *     tagged — this fires on initial page load (clears stale codes) and after
 *     every cart add/update (catches PDP adds against a stale-code cart).
 */
const UPSELL_DISCOUNT_CODE = 'UPSELL30';
const UPSELL_LINE_PROPERTY = '_upsell';

const collectSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);

class UpsellAddToCart extends HTMLElement {
  /** @type {number | null} */
  #resetTimeout = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  #handleClick = async () => {
    const button = this.querySelector('button');
    if (!button || button.disabled) return;

    const { variantId, productId } = this.dataset;
    if (!variantId) return;

    // Collect section IDs so the cart drawer re-renders after add (mirrors product-form.js)
    const sectionIds = collectSectionIds();

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: { [UPSELL_LINE_PROPERTY]: 'true' },
          ...(sectionIds.length && { sections: sectionIds.join(',') }),
        }),
      });

      if (!response.ok) throw new Error(`[upsell] cart/add.js ${response.status}`);

      let cart = await response.json();

      // Apply the upsell discount code, merging with any codes already on the cart.
      // This is the only path that applies UPSELL30 — product-page adds do not.
      //
      // /cart/add.js for a single item does not reliably return `discount_codes`
      // in its response, so we explicitly fetch /cart.js to read the current
      // codes. Without this, /cart/update.js below would replace existing codes
      // (e.g. BUNDLE15 from a bundle add) with just UPSELL30, dropping the
      // bundle discount.
      let existingCodes = readDiscountCodes(cart);
      if (!Array.isArray(cart.discount_codes)) {
        try {
          const currentCartResp = await fetch(`${window.Shopify.routes.root}cart.js`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (currentCartResp.ok) {
            existingCodes = readDiscountCodes(await currentCartResp.json());
          }
        } catch (err) {
          console.warn('[upsell] read existing discount codes failed:', err);
        }
      }

      const mergedCodes = existingCodes.includes(UPSELL_DISCOUNT_CODE)
        ? existingCodes
        : [...existingCodes, UPSELL_DISCOUNT_CODE];

      try {
        const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: mergedCodes.join(','),
            ...(sectionIds.length && { sections: sectionIds.join(',') }),
          }),
        });
        if (updateResponse.ok) cart = await updateResponse.json();
      } catch (err) {
        console.warn('[upsell] discount apply failed:', err);
      }

      this.dispatchEvent(
        new CartAddEvent(cart, 'upsell-add-to-cart', {
          source: 'upsell-add-to-cart',
          productId,
          itemCount: 1,
          sections: cart.sections ?? {},
        })
      );

      // Show checkmark: set data-added, let CSS transitions handle the visual,
      // then reset after the display duration.
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      button.dataset.added = 'true';
      this.#resetTimeout = setTimeout(() => {
        button.removeAttribute('data-added');
        this.#resetTimeout = null;
      }, 1500);
    } catch (err) {
      console.warn('[upsell] add to cart failed:', err);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('upsell-add-to-cart')) {
  customElements.define('upsell-add-to-cart', UpsellAddToCart);
}

/**
 * @param {{ discount_codes?: Array<{ code: string }> }} cart
 * @returns {string[]}
 */
function readDiscountCodes(cart) {
  return Array.isArray(cart.discount_codes)
    ? cart.discount_codes.map((d) => d.code).filter(Boolean)
    : [];
}

/**
 * @param {{ items?: Array<{ properties?: Record<string, string> | null }> }} cart
 */
function hasUpsellTaggedLine(cart) {
  if (!Array.isArray(cart.items)) return false;
  return cart.items.some((item) => {
    const props = item.properties;
    return props != null && props[UPSELL_LINE_PROPERTY] === 'true';
  });
}

let cleanupInFlight = false;

/**
 * Removes UPSELL30 from the cart if it's present but no line is tagged as
 * upsell-added. Idempotent and safe to call repeatedly.
 */
async function cleanupStaleUpsellDiscount() {
  if (cleanupInFlight) return;
  cleanupInFlight = true;
  try {
    const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();

    const codes = readDiscountCodes(cart);
    if (!codes.includes(UPSELL_DISCOUNT_CODE)) return;
    if (hasUpsellTaggedLine(cart)) return;

    const remaining = codes.filter((c) => c !== UPSELL_DISCOUNT_CODE);
    const sectionIds = collectSectionIds();

    const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        // Empty string clears all codes; otherwise pass the remaining ones.
        discount: remaining.join(','),
        ...(sectionIds.length && { sections: sectionIds.join(',') }),
      }),
    });
    if (!updateResponse.ok) return;
    const updatedCart = await updateResponse.json();

    document.dispatchEvent(new DiscountUpdateEvent(updatedCart, 'upsell-discount-cleanup'));
  } catch (err) {
    console.warn('[upsell] discount cleanup failed:', err);
  } finally {
    cleanupInFlight = false;
  }
}

// Run cleanup on every cart add/update. The upsell click itself fires a
// CartAddEvent, but it always leaves a tagged line in the cart, so cleanup
// is a no-op in that case. PDP adds (or the user emptying the cart of upsell
// items) leave no tagged line and trigger code removal.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  cleanupStaleUpsellDiscount();
});

// Run once on page load to clear codes left over from previous sessions.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', cleanupStaleUpsellDiscount, { once: true });
} else {
  cleanupStaleUpsellDiscount();
}
